import { FC, useEffect, useRef, useState } from 'react';

import { setFocusOnExtent, setMapMode } from 'store/mapSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { CenterActiveSourceGeometryButton } from 'components/CenterActiveSourceGeometryButton/CenterActiveSourceGeometryButton';
import { DrawerSection } from 'components/drawerSections/DrawerSections';
import RaaInputField from 'components/fieldComponents/raaInputField/RaaInputField';
import GeometryFileDownload from 'components/geometryFileDownload/GeometryFileDownload';
import { GeometryFileUploadInput } from 'components/geometryFileUpload/GeometryFileUpload';
import { activeSource, replaceSingleUtkastFeaturesInActiveSource } from 'components/map/Layers';
import { TextButton } from 'components/sharedElements/sharedElements';
import CategoriesWithTagList from 'components/utkast/categoriesWithTagList/CategoriesWithTagList';
import { EnskiltFormValues } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import {
  AndamalPut,
  AndamalTag,
  BebyggelseobjektDomaner,
  BebyggelseobjektUtkast,
  HuvudgruppTyp,
  HuvudkategoriTyp,
  UnderkategoriTyp
} from 'models';

import { BebyggelseobjektgruppEnum, MapModeEnum } from 'enums';

import { Feature } from 'ol';
import { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';

import { useFormikContext } from 'formik';
import { andamalTagToAndamalPut, convertFromAndamalToAndamalTag } from 'utilities/convertUtkastTypes';

interface SammansattGrundinformationSectionProps {
  utkastData: BebyggelseobjektUtkast;
  domaner: BebyggelseobjektDomaner;
}

const SammansattGrundinformationSection: FC<SammansattGrundinformationSectionProps> = ({ utkastData, domaner }) => {
  const { setFieldValue, setFieldTouched, touched, errors, values } = useFormikContext<EnskiltFormValues>();
  const dispatch = useAppDispatch();
  const { setDialog, unsetDialog } = useDialog();

  const [primaryAndamal, setPrimaryAndamal] = useState<Array<AndamalTag>>([]);
  const [convertingFile, setConvertingFile] = useState<boolean>(false);
  const [filteredKategorier, setFilteredKategorier] = useState<Array<HuvudgruppTyp>>([]);

  const fileUploadRef = useRef<HTMLInputElement>(null);

  const handleInteractionProceed = (
    featureCollection: GeoJSONFeatureCollection,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (featureCollection) {
      setFieldTouched('geometri');
      setFieldValue('geometri', featureCollection);
      replaceSingleUtkastFeaturesInActiveSource(featureCollection, utkastData);
      dispatch(
        setFocusOnExtent({
          type: 'fit',
          extent: activeSource.getExtent()
        })
      );
    }

    dispatch(setMapMode(MapModeEnum.UTKAST_REGISTRATION));
  };

  const removePrimaryAndamalTag = () => {
    if (primaryAndamal.length > 0 && !primaryAndamal[0].aktiv) {
      setDialog(RaaDialog, {
        header: 'Ta bort utfasat värde?',
        children: <p className="tw-my-4">Du kommer inte att kunna välja den här kategorin igen.</p>,
        cancel: () => unsetDialog(),
        cancelButtonText: 'Avbryt',
        proceedButtonText: 'Ta bort',
        proceed: async () => {
          setPrimaryAndamal([]);
          await setFieldValue('ursprungligt_primar_andamal', null);
          unsetDialog();
        },
        ariaDescribedBy: 'dialog-description'
      });
    } else {
      setPrimaryAndamal([]);
      void setFieldValue('ursprungligt_primar_andamal', null);
    }
  };

  useEffect(() => {
    if (utkastData && domaner) {
      if (domaner?.kategorityper) {
        setFilteredKategorier(
          domaner.kategorityper
            .filter((huvudgrupp: HuvudgruppTyp) => huvudgrupp.aktiv)
            .map((huvudgrupp: HuvudgruppTyp) => ({
              ...huvudgrupp,
              huvudkategorier: huvudgrupp.huvudkategorier
                .filter((huvudkategori: HuvudkategoriTyp) => huvudkategori.aktiv)
                .map((huvudkategori: HuvudkategoriTyp) => ({
                  ...huvudkategori,
                  underkategorier: huvudkategori.underkategorier.filter(
                    (underkategori: UnderkategoriTyp) => underkategori.aktiv
                  )
                }))
            }))
        );
        if (utkastData?.ursprungligt_primar_andamal) {
          const res = convertFromAndamalToAndamalTag([utkastData.ursprungligt_primar_andamal], domaner.kategorityper);
          setPrimaryAndamal(res);
        }
      }
    }
  }, [utkastData, domaner]);

  return (
    <DrawerSection>
      <fieldset>
        <legend className="tw-heading-md tw-mb-4">Grundinformation</legend>

        <div className="tw-mb-5">
          <label className="raa-label" htmlFor="namn">
            Byggnadsminnets namn *
          </label>
          {utkastData?.lm_registerbyggnad?.byggnadnamn && utkastData?.lm_registerbyggnad.byggnadnamn.length > 0 && (
            <TextButton
              icon="raa-icon-cut"
              onClick={() => setFieldValue('namn', utkastData.lm_registerbyggnad?.byggnadnamn[0])}
            >
              Klistra in Lantmäteriets namn ({utkastData.lm_registerbyggnad?.byggnadnamn[0]})
            </TextButton>
          )}
          <RaaInputField name="namn" placeholder="Namn enligt beslut" shouldValidateForm={true} />
        </div>

        <div className="tw-mb-5">
          <h2 className="raa-label">Geometri *</h2>

          <div className="tw-flex tw-justify-between">
            {convertingFile && <span className="tw-italic tw-text-raa-gray-5">Konverterar filen...</span>}
            {!convertingFile && (
              <>
                <div className="tw-flex tw-gap-2">
                  <TextButton icon="raa-icon-edit" disabled={true}>
                    Ändra
                  </TextButton>
                  <GeometryFileUploadInput
                    id="geometri"
                    converting={(converting: boolean) => setConvertingFile(converting)}
                    success={(features: Feature[]) =>
                      handleInteractionProceed(features, setFieldTouched, setFieldValue)
                    }
                    labelText={'Ny från fil'}
                    showReplaceWarning={!!values.geometri}
                    fileUploadRef={fileUploadRef}
                  />
                </div>
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  <CenterActiveSourceGeometryButton />
                  {utkastData.geometri && (
                    <GeometryFileDownload
                      fileName={utkastData.bebyggelsenummer || 'Okänd'}
                      geometry={utkastData.geometri}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <p className="tw-mt-1 tw-text-raa-error-red">{errors.geometri && touched.geometri && errors.geometri}</p>
        </div>
        <CategoriesWithTagList
          categories={filteredKategorier}
          bebyggelseobjekgrupp={BebyggelseobjektgruppEnum.OMRADE}
          label={'Ändamål *'}
          id="ursprungligt_primar_andamal"
          onRemoveTag={() => removePrimaryAndamalTag()}
          setTagList={(andamalTags: Array<AndamalTag>) => {
            setPrimaryAndamal((current) => [...current, ...andamalTags]);
            const ursprungligtPrimarAndamal: AndamalPut[] = [...primaryAndamal, ...andamalTags].map(
              andamalTagToAndamalPut
            );
            void setFieldValue('ursprungligt_primar_andamal', ursprungligtPrimarAndamal[0]);
          }}
          tagList={primaryAndamal}
          categoriesSelectDisabled={primaryAndamal.length > 0}
          hasError={Boolean(errors.ursprungligt_primar_andamal)}
        />
        <p hidden={Boolean(!errors.ursprungligt_primar_andamal)} className="tw-mt-1 tw-text-raa-error-red">
          {errors.ursprungligt_primar_andamal}
        </p>
      </fieldset>
    </DrawerSection>
  );
};

export default SammansattGrundinformationSection;
